import { useApprovalFlows } from '@/accessControl/hooks/useApprovalFlows.hooks';
import { useProductionVersionStatus } from '@/accessControl/hooks/useProductionVersionStatus.hooks';
import { sqReportStore, sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useWorkbookNames } from '@/reportEditor/hooks/useWorkbookNames.hooks';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import {
  isApprovalFlowsEnabled,
  isApprovalFlowsForOrganizerTopicsEnabled,
} from '@/services/systemConfiguration.utilities';
import { isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import type { Content } from '@/reportEditor/report.constants';

/**
 * Why a source is flagged. Both are read straight off its version history, and they differ in what can be done:
 * DIFFERENT_VERSION has a production version to move the content to, NONE_IN_PRODUCTION has nothing to point at.
 */
export type NonProductionReason = 'DIFFERENT_VERSION' | 'NONE_IN_PRODUCTION';

export type NonProductionSource = {
  id: string;
  name: string;
  reason: NonProductionReason;
  /**
   * One piece of this source's content in the Topic, used to link to the source. The content API's sourceUrl
   * redirect is keyed by content, not by workbook, so a source showing several pieces of content is represented by
   * the first of them; they all resolve to the same source workbook.
   */
  contentId: string | undefined;
};

export type NonProductionContentSources = {
  /** Source workbooks a readable version history confirms are not the production version. */
  notInProduction: NonProductionSource[];
  /** How many pieces of content sit on those sources. */
  affectedContentCount: number;
  /** True while the check is in flight, so callers can hold the banner back rather than flash it. */
  isChecking: boolean;
};

const NOTHING_TO_REPORT: NonProductionContentSources = {
  notInProduction: [],
  affectedContentCount: 0,
  isChecking: false,
};

/**
 * Reports content from source workbooks that are not in production in two cases: the Topic is itself in production,
 * or an approval flow is attached, starting from the first step. A Topic with no flow attached has made no production
 * promise, and is left unreported.
 *
 * Scoped to the document being viewed, not the whole Topic: `sqReportStore` holds one report, so a Topic with
 * several documents is judged one at a time and the banner says so. Covering a whole Topic from here would mean
 * loading every document's content client-side; it belongs to the server-side walk in CRAB-64778.
 *
 * Known gap, deliberately left to the follow-on work (CRAB-60759 discussion): every question here is asked as the
 * viewer, and a Version History carries the ACEs of whoever locked and versioned the item rather than inheriting
 * from the workbook (SPEC/appserver/ITEM_VERSIONING.md §4.4). A read-only Organizer audience therefore cannot read
 * the histories at all -- not even the Topic's -- and gets no banner, which is precisely the audience that most
 * needs to know whether what it is looking at is production-backed. Reporting sources whose status simply could not
 * be established would warn about healthy Topics far more often than stale ones, so they are left unreported here.
 * Answering this correctly means asking it server-side, where the content walk can run as system the way
 * `TopicLocker.validate()` already does.
 */
export const useNonProductionContentSources = (): NonProductionContentSources => {
  const isTopic = useWorkbookIsTopic();
  const topicId = useFluxPath(sqWorkbenchStore, (store) => store.stateParams.workbookId);
  const contentNotArchived: Content[] = useFluxPath(sqReportStore, () => sqReportStore.contentNotArchived);
  const { hasApprovalFlowAttached } = useApprovalFlows();

  // Both flags are required, matching how a Topic reaches an approval flow in the first place: Organizer Topics ride
  // on the base feature, behind their own flag on top of it.
  const approvalFlowsEnabledForTopics = isApprovalFlowsEnabled() && isApprovalFlowsForOrganizerTopicsEnabled();
  // Suppressed for presentation mode and headless capture: both produce output for an audience or a PDF, where an
  // authoring warning is noise rather than information.
  const suppressed = !isTopic || !approvalFlowsEnabledForTopics || headlessRenderMode() || isPresentationWorkbookMode();

  // The Topic itself is checked alongside this document's sources so the whole question costs a single request.
  const sourceIds = [
    ...new Set(
      contentNotArchived
        .map(({ workbookId }) => workbookId)
        .filter((workbookId): workbookId is string => Boolean(workbookId) && workbookId !== topicId),
    ),
  ];
  const itemIds = suppressed || !topicId || sourceIds.length === 0 ? [] : [topicId, ...sourceIds];

  const { inProductionItemIds, confirmedItemIds, itemNamesById, historyHasProductionVersionByItemId, isLoading } =
    useProductionVersionStatus(itemIds, { enabled: itemIds.length > 0 });

  // Only sources a version history positively reports as stale are flagged. One whose history did not come back is
  // unknown rather than stale -- see the note above -- and saying so would cry wolf at every read-only viewer.
  const isTopicInProduction = Boolean(topicId) && inProductionItemIds.has(topicId!);
  const shouldReport = isTopicInProduction || hasApprovalFlowAttached;
  const flaggedSourceIds =
    itemIds.length === 0 || !shouldReport
      ? []
      : sourceIds.filter((id) => confirmedItemIds.has(id) && !inProductionItemIds.has(id));

  // Names are looked up only for sources actually being reported. Passing every source would fire a request per
  // Analysis on every Topic open -- including the suppressed paths, where nothing is rendered at all.
  const namesById = useWorkbookNames(flaggedSourceIds, itemNamesById);

  if (itemIds.length === 0) {
    return NOTHING_TO_REPORT;
  }

  // Only the first load withholds the banner. isFetching also covers background refetches, and using it here would
  // tear the banner down and put it back on every window refocus, shifting the document under the reader.
  if (isLoading) {
    return { ...NOTHING_TO_REPORT, isChecking: true };
  }

  if (!shouldReport) {
    return NOTHING_TO_REPORT;
  }

  const notInProduction = flaggedSourceIds.map((workbookId) => ({
    id: workbookId,
    name: namesById[workbookId] ?? '',
    reason: (historyHasProductionVersionByItemId[workbookId]
      ? 'DIFFERENT_VERSION'
      : 'NONE_IN_PRODUCTION') as NonProductionReason,
    contentId: contentNotArchived.find((contentItem) => contentItem.workbookId === workbookId)?.id,
  }));
  const flaggedIds = new Set(notInProduction.map(({ id }) => id));

  return {
    notInProduction,
    affectedContentCount: contentNotArchived.filter(({ workbookId }) => flaggedIds.has(workbookId)).length,
    isChecking: false,
  };
};
